<template>
  <!-- 
  主应用组件 App.vue
  职责:
  1. 管理全局布局（导航栏、主内容、页脚）
  2. 管理用户认证状态（登录、注册、退出）
  3. 管理全局网站设置
  4. 处理页面切换和路由导航
  
  布局结构:
  ┌─────────────────────────────────────────────────────────┐
  │                    导航栏 (navbar)                       │
  │  Logo | 首页 | 文案 | 图像 | 视频 | 短剧 | 订阅         │
  │                              登录/注册/退出             │
  ├─────────────────────────────────────────────────────────┤
  │                    主内容区域 (main-content)              │
  │  <router-view> 根据路由渲染对应页面组件                   │
  ├─────────────────────────────────────────────────────────┤
  │                    页脚 (footer)                         │
  │  Logo | 链接 | 联系方式 | 版权信息                        │
  └─────────────────────────────────────────────────────────┘
  -->
  <div id="app">
    <!-- 导航栏 - 仅在非管理员页面和非独立页面显示 -->
    <nav class="navbar" v-if="!isAdminPage && !isStandalonePage">
      <div class="nav-container">
        <!-- Logo区域 -->
        <div class="logo">
          <img v-if="siteSetting.logoImage" :src="siteSetting.logoImage" alt="Logo" class="logo-img">
          <span v-else class="logo-icon">🧠</span>
          <span class="logo-text">{{ siteSetting.siteTitle }}</span>
        </div>
        
        <!-- 导航菜单 -->
        <ul class="nav-menu">
          <li :class="{active:currentPage=='home'}" @click="switchPage('home')">首页</li>
          <li :class="{active:currentPage=='live'}" @click="switchPage('live')">文案创作</li>
          <li :class="{active:currentPage=='image'}" @click="switchPage('image')">图像创作</li>
          <li :class="{active:currentPage=='video'}" @click="switchPage('video')">视频创作</li>
          <li :class="{active:currentPage=='drama'}" @click="switchPage('drama')">短剧创作</li>
          <li :class="{active:currentPage=='openclaw'}" @click="switchPage('openclaw')">OpenClaw</li>
          <li :class="{active:currentPage=='code'}" @click="switchPage('code')">代码生成</li>
          <li :class="{active:currentPage=='price'}" @click="switchPage('price')">订阅方式</li>
        </ul>
        
        <!-- 用户操作区域 -->
        <div class="user-nav">
          <button v-if="!userInfo" class="btn-login" @click="openLogin">登录</button>
          <button v-if="!userInfo" class="btn-register" @click="openRegister">注册</button>
          <div v-else class="user-info">
            <span class="user-email">{{ userInfo.email }}</span>
            <button class="btn-logout" @click="logout">退出</button>
            <button class="btn-profile" @click="goToProfile">个人中心</button>
          </div>
          <button v-if="userInfo" class="btn-console" @click="goToConsole">控制台</button>
        </div>
      </div>
    </nav>

    <!-- 主内容区域 -->
    <main :class="['main-content', { 'admin-content': isAdminPage, 'standalone-content': isStandalonePage }]">
      <!-- 只有首页模块使用简单的淡入淡出效果 -->
      <transition v-if="!isStandalonePage && !isAdminPage" name="page-fade" mode="out-in">
        <router-view @open-register="openRegister" :key="$route.path"/>
      </transition>
      <!-- 生成页面和后台管理页面直接渲染，无转场 -->
      <router-view v-else @open-register="openRegister" :key="$route.path"/>
    </main>

    <!-- 底部 -->
    <footer class="footer" v-if="!isAdminPage && !isStandalonePage">
      <div class="container">
        <div class="footer-content">
          <div class="logo-footer">
            <img v-if="siteSetting.logoImage" :src="siteSetting.logoImage" alt="Logo" class="footer-logo-img">
            <span v-else>🧠</span>
            {{ siteSetting.siteTitle }}
          </div>
          <p>{{ siteSetting.siteDescription }}</p>
          <div class="links">
            <a @click="switchPage('home')">首页</a>
            <a @click="switchPage('price')">价格</a>
            <a @click="$router.push('/terms')">用户协议</a>
            <a @click="$router.push('/privacy')">隐私政策</a>
          </div>
          <div class="contact-info" v-if="siteSetting.contactEmail || siteSetting.contactPhone">
            <span v-if="siteSetting.contactEmail">邮箱: {{ siteSetting.contactEmail }}</span>
            <span v-if="siteSetting.contactEmail && siteSetting.contactPhone"> | </span>
            <span v-if="siteSetting.contactPhone">电话: {{ siteSetting.contactPhone }}</span>
          </div>
          <div class="copyright" v-if="siteSetting.copyright">{{ siteSetting.copyright }}</div>
          <div class="icp" v-if="siteSetting.ICP">{{ siteSetting.ICP }}</div>
        </div>
      </div>
    </footer>

    <!-- 登录弹窗 -->
    <div class="modal-overlay" v-if="showLogin" @click.self="closeModals">
      <div class="modal-box">
        <div class="modal-close" @click="closeModals">×</div>
        <h3>账号登录</h3>
        <div class="form-item">
          <label>邮箱</label>
          <input type="email" v-model="loginForm.email" placeholder="请输入注册邮箱">
        </div>
        <div class="form-item">
          <label>密码</label>
          <input type="password" v-model="loginForm.pwd" placeholder="请输入密码">
        </div>
        <button class="btn modal-btn" @click="doLogin">登录</button>
        <div class="modal-tip" @click="goRegister">没有账号？立即注册</div>
        <div class="modal-tip forgot-pwd" @click="openForgotPassword">忘记密码？</div>
      </div>
    </div>

    <!-- 忘记密码弹窗 -->
    <div class="modal-overlay" v-if="showForgotPassword" @click.self="closeModals">
      <div class="modal-box">
        <div class="modal-close" @click="closeModals">×</div>
        <h3>忘记密码</h3>
        <div class="form-item">
          <label>邮箱</label>
          <input type="email" v-model="forgotForm.email" placeholder="请输入注册邮箱">
        </div>
        <div class="form-item">
          <label>验证码</label>
          <div class="code-row">
            <input type="text" v-model="forgotForm.code" placeholder="邮箱验证码">
            <button class="code-btn" @click="sendForgotCode" :disabled="forgotCountdown > 0">
              {{ forgotCountdown > 0 ? forgotCountdown + '秒后重发' : '获取验证码' }}
            </button>
          </div>
        </div>
        <div class="form-item">
          <label>新密码</label>
          <input type="password" v-model="forgotForm.pwd" placeholder="设置新密码">
        </div>
        <div class="form-item">
          <label>确认密码</label>
          <input type="password" v-model="forgotForm.pwdConfirm" placeholder="再次输入密码">
        </div>
        <button class="btn modal-btn" @click="doForgotPassword" :disabled="forgotSubmitting">
          {{ forgotSubmitting ? '修改中...' : '确认修改' }}
        </button>
        <div class="modal-tip" @click="goLogin">返回登录</div>
      </div>
    </div>

    <!-- 注册弹窗 -->
    <div class="modal-overlay" v-if="showRegister" @click.self="closeModals">
      <div class="modal-box">
        <div class="modal-close" @click="closeModals">×</div>
        <h3>账号注册</h3>
        <div class="form-item">
          <label>邮箱</label>
          <input type="email" v-model="regForm.email" placeholder="请输入常用邮箱">
        </div>
        <div class="form-item">
          <label>密码</label>
          <input type="password" v-model="regForm.pwd" placeholder="设置登录密码">
        </div>
        <div class="form-item">
          <label>确认密码</label>
          <input type="password" v-model="regForm.pwdConfirm" placeholder="再次输入密码">
        </div>
        <div class="form-item">
          <label>验证码</label>
          <div class="code-row">
            <input type="text" v-model="regForm.code" placeholder="邮箱验证码">
            <button class="code-btn" @click="sendRegCode" :disabled="countdown > 0">
              {{ countdown > 0 ? countdown + '秒后重发' : '获取验证码' }}
            </button>
          </div>
        </div>
        <div class="form-item terms-item">
          <label class="terms-label">
            <input type="checkbox" v-model="regForm.agreedToTerms">
            <span>我已阅读并同意 <router-link to="/terms" target="_blank" class="terms-link">《用户服务协议》</router-link></span>
          </label>
        </div>
        <button class="btn modal-btn" @click="doRegister" :disabled="regSubmitting || !regForm.agreedToTerms">
          {{ regSubmitting ? '注册中...' : '注册' }}
        </button>
        <div class="modal-tip" @click="goLogin">已有账号？去登录</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import siteConfig from './config/siteConfig'
import { checkLogin, protectedPages } from './utils/auth'
import appCache from './utils/appCache'

export default {
  name: 'App',
  data() {
    return {
      currentPage: 'home',
      // 弹窗控制
      showLogin: false,
      showRegister: false,
      // 登录表单
      loginForm: {
        email: '',
        pwd: ''
      },
      // 注册表单
      regForm: {
        email: '',
        pwd: '',
        pwdConfirm: '',
        code: '',
        agreedToTerms: false
      },
      // 验证码倒计时
      countdown: 0,
      // 注册提交状态
      regSubmitting: false,
      // 忘记密码弹窗控制
      showForgotPassword: false,
      // 忘记密码表单
      forgotForm: {
        email: '',
        code: '',
        pwd: '',
        pwdConfirm: ''
      },
      // 忘记密码验证码倒计时
      forgotCountdown: 0,
      // 忘记密码提交状态
      forgotSubmitting: false,
      // 用户登录状态
      userInfo: null, // 登录后存放 { id, email, token }
      // 网站设置（从本地配置和数据库配置合并）
      siteSetting: {
        ...siteConfig,
        copyright: '© 2025 韦小布AI 版权所有',
        ICP: '京ICP备12345678号',
        contactEmail: 'lirui9894@qq.com',
        contactPhone: '13800138000'
      }
    }
  },
  computed: {
    isAdminPage() {
      return this.$route.path.startsWith('/admin')
    },
    isStandalonePage() {
      return this.$route.path === '/video-generator-pro' || this.$route.path === '/text-generator-pro' || this.$route.path === '/image-generator' || this.$route.path === '/drama-generator-pro'
    }
  },
  created() {
    // 页面加载时自动读取登录状态
    this.loadUserInfo()
    // 加载网站设置
    this.loadSiteSetting()
    // 预加载所有数据到缓存
    this.loadAppCache()
    // 暴露方法到全局
    window.$app = this
    // 暴露缓存到全局
    window.$appCache = appCache
    // 监听自定义事件
    this.$on('need-login', this.openLogin)
  },
  mounted() {
    // 应用全局背景图片
    this.applyBackgroundImage()
  },
  methods: {
    // 预加载应用缓存
    async loadAppCache() {
      try {
        console.log('正在预加载所有数据...')
        await appCache.preloadAll()
        console.log('所有数据预加载完成')
      } catch (error) {
        console.error('预加载数据失败:', error)
      }
    },
    // 应用全局背景图片
    applyBackgroundImage() {
      if (siteConfig.backgroundImage) {
        // 同时设置 body 和 html 的背景图片，确保覆盖整个屏幕
        // 移除背景色，确保背景图片能够显示
        document.documentElement.style.backgroundColor = 'transparent'
        document.documentElement.style.backgroundImage = `url(${siteConfig.backgroundImage})`
        document.documentElement.style.backgroundSize = 'cover'
        document.documentElement.style.backgroundPosition = 'center'
        document.documentElement.style.backgroundRepeat = 'no-repeat'
        document.documentElement.style.backgroundAttachment = 'fixed'
        
        document.body.style.backgroundColor = 'transparent'
        document.body.style.backgroundImage = `url(${siteConfig.backgroundImage})`
        document.body.style.backgroundSize = 'cover'
        document.body.style.backgroundPosition = 'center'
        document.body.style.backgroundRepeat = 'no-repeat'
        document.body.style.backgroundAttachment = 'fixed'
      } else {
        // 清除背景图片，恢复默认背景色
        document.documentElement.style.backgroundColor = '#0d0f16'
        document.documentElement.style.backgroundImage = 'none'
        
        document.body.style.backgroundColor = '#0d0f16'
        document.body.style.backgroundImage = 'none'
      }
    },
    // 加载网站设置
    loadSiteSetting() {
      axios.get('/api/site-setting')
        .then(response => {
          if (response.data.code === 200) {
            // 合并数据库配置和本地配置，数据库配置优先
            this.siteSetting = {
              ...siteConfig, // 本地配置（包含 backgroundImage 和 logoImage）
              ...response.data.data, // 数据库配置（其他设置）
              backgroundImage: siteConfig.backgroundImage || response.data.data.backgroundImage,
              logoImage: siteConfig.logoImage || response.data.data.logoImage
            }
          }
        })
        .catch(error => {
          console.error('获取网站设置失败:', error)
        })
    },
    // 切换页面
    switchPage(page) {
      // 检查是否是需要登录的页面
      if (protectedPages.includes(page)) {
        const auth = checkLogin()
        if (!auth.isLoggedIn) {
          alert('请先登录后才能使用此功能')
          this.openLogin()
          return
        }
      }
      
      this.currentPage = page
      window.scrollTo(0, 0)
      // 根据页面路由跳转
      switch(page) {
        case 'home':
          this.$router.push('/')
          break
        case 'image':
          this.$router.push('/image')
          break
        case 'video':
          this.$router.push('/video')
          break
        case 'drama':
          this.$router.push('/drama')
          break
        case 'openclaw':
          this.$router.push('/openclaw')
          break
        case 'code':
          this.$router.push('/code')
          break
        case 'price':
          this.$router.push('/price')
          break
        case 'live':
          this.$router.push('/text')
          break
        default:
          this.$router.push('/')
      }
    },
    // 跳转到控制台（文案生成页面）
    goToConsole() {
      this.$router.push('/text-generator-pro')
    },
    // 跳转到个人中心
    goToProfile() {
      this.$router.push('/profile')
    },
    // 加载本地登录状态
    loadUserInfo() {
      const user = localStorage.getItem('userInfo')
      if (user) {
        this.userInfo = JSON.parse(user)
      }
    },
    // 打开/关闭弹窗
    openLogin() {
      this.showLogin = true
      this.showRegister = false
    },
    openRegister() {
      this.showRegister = true
      this.showLogin = false
    },
    closeModals() {
      this.showLogin = false
      this.showRegister = false
      this.showForgotPassword = false
    },
    // 登录 ↔ 注册 切换
    goRegister() {
      this.showLogin = false
      this.showRegister = true
    },
    goLogin() {
      this.showRegister = false
      this.showForgotPassword = false
      this.showLogin = true
    },
    // 打开忘记密码
    openForgotPassword() {
      this.showLogin = false
      this.showForgotPassword = true
    },
    // 发送忘记密码验证码
    sendForgotCode() {
      if (!this.forgotForm.email) {
        alert('请先输入邮箱')
        return
      }

      if (this.forgotCountdown > 0) {
        return
      }

      axios.post('/api/auth/verification-code', {
        email: this.forgotForm.email
      }).then(() => {
        alert('验证码已发送到您的邮箱')
        this.forgotCountdown = 60
        const timer = setInterval(() => {
          this.forgotCountdown--
          if (this.forgotCountdown <= 0) {
            clearInterval(timer)
          }
        }, 1000)
      }).catch(error => {
        alert('发送失败：' + error.response.data.detail)
      })
    },
    // 忘记密码（修改密码）
    doForgotPassword() {
      if (this.forgotSubmitting) {
        return
      }

      if (!this.forgotForm.email || !this.forgotForm.code || !this.forgotForm.pwd || !this.forgotForm.pwdConfirm) {
        alert('请完善所有信息')
        return
      }

      if (this.forgotForm.pwd.length < 6) {
        alert('密码长度不能少于6位')
        return
      }

      if (this.forgotForm.pwd !== this.forgotForm.pwdConfirm) {
        alert('两次输入的密码不一致')
        return
      }

      this.forgotSubmitting = true

      axios.post('/api/auth/reset-password', {
        email: this.forgotForm.email,
        code: this.forgotForm.code,
        password: this.forgotForm.pwd
      }).then(() => {
        alert('密码修改成功，请登录')
        this.forgotSubmitting = false
        this.goLogin()
      }).catch(error => {
        this.forgotSubmitting = false
        let errorMessage = '修改失败，请稍后重试'
        if (error.response && error.response.data) {
          if (error.response.data.detail) {
            errorMessage = '修改失败：' + error.response.data.detail
          } else if (error.response.data.message) {
            errorMessage = '修改失败：' + error.response.data.message
          }
        }
        alert(errorMessage)
      })
    },
    // 发送注册验证码
    sendRegCode() {
      if (!this.regForm.email) {
        alert('请先输入邮箱')
        return
      }

      if (this.countdown > 0) {
        return
      }

      // 调用后端API
      axios.post('/api/auth/verification-code', {
        email: this.regForm.email
      }).then(() => {
        alert('验证码已发送到您的邮箱')
        // 开始倒计时
        this.countdown = 60
        const timer = setInterval(() => {
          this.countdown--
          if (this.countdown <= 0) {
            clearInterval(timer)
          }
        }, 1000)
      }).catch(error => {
        let errorMessage = '验证码发送失败，请稍后重试'
        if (error.response && error.response.data) {
          if (error.response.data.detail) {
            errorMessage = '验证码发送失败：' + error.response.data.detail
          }
        }
        alert(errorMessage)
      })
    },
    // 登录
    doLogin() {
      if (!this.loginForm.email || !this.loginForm.pwd) {
        alert('请填写邮箱和密码')
        return
      }

      // 调用后端API
      axios.post('/api/auth/login', {
        email: this.loginForm.email,
        password: this.loginForm.pwd
      }).then(response => {
        // 保存状态
        const user = {
          id: response.data.user.id,
          email: response.data.user.email,
          token: 'demo-token-' + Date.now()
        }
        this.userInfo = user
        localStorage.setItem('userInfo', JSON.stringify(user))
        alert('登录成功')
        this.closeModals()
      }).catch(error => {
        alert('登录失败：' + error.response.data.detail)
      })
    },
    // 注册
    doRegister() {
      // 防止重复提交
      if (this.regSubmitting) {
        return
      }

      // 验证所有字段
      if (!this.regForm.email || !this.regForm.pwd || !this.regForm.pwdConfirm || !this.regForm.code) {
        alert('请完善所有信息')
        return
      }

      // 验证密码长度
      if (this.regForm.pwd.length < 6) {
        alert('密码长度不能少于6位')
        return
      }

      // 验证两次密码是否一致
      if (this.regForm.pwd !== this.regForm.pwdConfirm) {
        alert('两次输入的密码不一致，请重新输入')
        return
      }

      // 设置提交状态
      this.regSubmitting = true

      // 调用后端API
      axios.post('/api/auth/register', {
        email: this.regForm.email,
        password: this.regForm.pwd,
        code: this.regForm.code
      }).then(() => {
        alert('注册成功，请登录')
        this.regSubmitting = false
        this.goLogin()
      }).catch(error => {
        this.regSubmitting = false
        console.log('错误详情:', error)
        let errorMessage = '注册失败，请稍后重试'
        if (error.response && error.response.data) {
          if (error.response.data.detail) {
            errorMessage = '注册失败：' + error.response.data.detail
          } else if (error.response.data.message) {
            errorMessage = '注册失败：' + error.response.data.message
          }
        }
        alert(errorMessage)
      })
    },
    // 退出登录
    logout() {
      this.userInfo = null
      localStorage.removeItem('userInfo')
      alert('已退出登录')
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

html, body {
  color: #fff;
  line-height: 1.6;
  overflow-x: hidden;
  overflow-y: auto;
  height: auto;
  min-height: 100%;
}

#app {
  min-height: 100vh;
  height: auto;
  background: transparent;
}

/* 全局背景图片 - 固定背景 */
body {
  background: #0d0f16;
  min-height: 100vh;
}

/* 导航 - 毛玻璃透明效果 */
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background: rgba(15, 18, 30, 0.75);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  z-index: 990;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}
.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
}

.user-nav {
  display: flex;
  align-items: center;
  gap: 15px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
}

.logo-img {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  object-fit: cover;
}

.logo-icon {
  font-size: 24px;
  color: #00e6ac;
}

.logo-text {
  font-size: 20px;
  font-weight: 600;
  color: #00e6ac;
}

.logo-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
  margin-bottom: 15px;
}

.footer-logo-img {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  object-fit: cover;
}
.nav-menu {
  display: flex;
  list-style: none;
  gap: 28px;
}
.nav-menu li {
  color: #ccc;
  cursor: pointer;
  font-size: 15px;
  transition: color 0.2s;
  position: relative;
}
.nav-menu li:hover,
.nav-menu li.active {
  color: #00e6ac;
}
.nav-menu li.active::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 0;
  width: 100%;
  height: 2px;
  background: #00e6ac;
  border-radius: 1px;
}

/* 用户栏 */
.user-nav {
  display: flex;
  gap: 12px;
  align-items: center;
}
.btn-login, .btn-register {
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: 0.2s;
  border: none;
}
.btn-login {
  background: transparent;
  color: #00e6ac;
  border: 1px solid #00e6ac;
}
.btn-login:hover {
  background: #00e6ac;
  color: #000;
}
.btn-register {
  background: #00e6ac;
  color: #000;
  font-weight: 500;
}
.btn-register:hover {
  background: #00c292;
}
.btn-console {
  background: #00e6ac;
  color: #000;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: 0.2s;
  border: none;
  margin-left: auto;
}
.btn-console:hover {
  background: #00c292;
}
.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}
.btn-profile {
    padding: 6px 14px;
    background: rgba(0, 230, 172, 0.15);
    color: #00e6ac;
    border: 1px solid rgba(0, 230, 172, 0.3);
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    transition: 0.2s;
}
.btn-profile:hover {
    background: rgba(0, 230, 172, 0.25);
}
.user-email {
  color: #aaa;
  font-size: 14px;
}
.btn-logout {
  padding: 6px 12px;
  background: #222;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
}
.btn-logout:hover {
  background: #333;
}

/* 主内容区域 */
.main-content {
  padding-top: 80px; /* 为固定导航栏留出空间 */
  background: transparent;
  box-sizing: border-box;
  min-height: calc(100vh - 80px);
  height: auto;
  width: 100%;
  overflow-y: auto;
}

/* 独立页面内容区域 */
.main-content.standalone-content {
  padding-top: 0;
  min-height: 100vh;
}

/* 后台内容区域 */
.admin-content {
  padding-top: 0; /* 后台页面不需要顶部空间 */
  min-height: 100vh;
}

/* 通用容器 */
.container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;
}
.section {
  padding: 120px 0 60px;
  box-sizing: border-box;
}

/* 按钮样式 */
.btn {
  padding: 14px 32px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: 0.2s;
  background: #1a1e2d;
  color: #00e6ac;
  border: 1px solid #00e6ac;
}
.btn:hover {
  background: rgba(0, 230, 172, 0.1);
}
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn.primary {
  background: #00e6ac;
  color: #000;
}
.btn.primary:hover {
  background: #00c292;
}
.btn.second {
  background: transparent;
  color: #00e6ac;
  border: 1px solid #00e6ac;
}
.btn.second:hover {
  background: rgba(0,230,172,0.1);
}

/* 底部 */
.footer {
  background: rgba(13, 15, 22, 0.9);
  border-top: 1px solid rgba(255,255,255,0.05);
  padding: 50px 0 30px;
  text-align: center;
}
.logo-footer {
  font-size: 20px;
  color: #00e6ac;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
}
.footer-logo-img {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  object-fit: cover;
}
.footer p {
  color: #666;
  margin-bottom: 20px;
}
.links {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;
}
.links a {
  color: #666;
  cursor: pointer;
  font-size: 14px;
}
.links a:hover {
  color: #00e6ac;
}
.copyright {
  color: #444;
  font-size: 13px;
  margin-top: 10px;
}

.contact-info {
  color: #666;
  font-size: 14px;
  margin-top: 15px;
}

.icp {
  color: #444;
  font-size: 12px;
  margin-top: 8px;
}

/* 弹窗 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.modal-box {
  background: #11141d;
  border: 1px solid rgba(0,230,172,0.2);
  border-radius: 16px;
  width: 100%;
  max-width: 420px;
  padding: 40px 30px;
  position: relative;
}
.modal-close {
  position: absolute;
  top: 16px;
  right: 20px;
  font-size: 22px;
  color: #888;
  cursor: pointer;
}
.modal-close:hover {
  color: #fff;
}
.modal-box h3 {
  text-align: center;
  margin-bottom: 30px;
  font-size: 24px;
}
.form-item {
  margin-bottom: 20px;
}
.form-item label {
  display: block;
  margin-bottom: 8px;
  color: #fff;
  font-size: 14px;
}
.form-item input {
  width: 100%;
  padding: 12px 16px;
  background: #1a1e2d;
  border: 1px solid #333;
  border-radius: 8px;
  color: #fff;
  outline: none;
}
.form-item input:focus {
  border-color: #00e6ac;
}
.code-row {
  display: flex;
  gap: 10px;
}
.code-row input {
  flex: 1;
}
.code-btn {
  padding: 12px 16px;
  background: #1a1e2d;
  border: 1px solid #333;
  border-radius: 8px;
  color: #00e6ac;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
}
.code-btn:hover:not(:disabled) {
  border-color: #00e6ac;
}
.code-btn:disabled {
  color: #555;
  cursor: not-allowed;
}

/* 用户协议复选框样式 */
.terms-item {
  margin-bottom: 20px;
}
.terms-label {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #999;
  line-height: 1.5;
}
.terms-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  margin-top: 2px;
  accent-color: #00e6ac;
  cursor: pointer;
  flex-shrink: 0;
}
.terms-link {
  color: #00e6ac;
  text-decoration: none;
  font-weight: 500;
}
.terms-link:hover {
  text-decoration: underline;
}

.modal-btn {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #00e6ac, #00c49a);
  border: none;
  border-radius: 8px;
  color: #000;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}
.modal-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0,230,172,0.3);
}
.modal-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}
.code-row input {
  flex: 1;
}
.code-btn {
  padding: 12px 16px;
  background: #0099ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
}
.modal-btn {
  width: 100%;
  margin-top: 10px;
}
.modal-tip {
  text-align: center;
  margin-top: 20px;
  color: #0099ff;
  cursor: pointer;
  font-size: 14px;
}

/* 响应式 */
@media (max-width: 768px) {
  .nav-container {
    flex-direction: column;
    gap: 10px;
    padding: 12px 24px;
  }
  .nav-menu {
    gap: 14px;
    flex-wrap: wrap;
    justify-content: center;
  }
  .user-nav {
    width: 100%;
    justify-content: center;
  }
  .main-content {
    padding-top: 120px;
  }
}

/* ==================== 页面过渡动画 ==================== */

/* 方案1：滑入滑出 - 从左/从右 */
.page-slide-enter-active,
.page-slide-leave-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.page-slide-enter-from {
  transform: translateX(30px);
  opacity: 0;
}
.page-slide-leave-to {
  transform: translateX(-30px);
  opacity: 0;
}

/* 方案2：淡入淡出 + 缩放 - 经典优雅 */
.page-fade-enter-active,
.page-fade-leave-active {
  transition: all 0.3s ease;
}
.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
  transform: scale(0.97);
}

/* 方案3：弹出效果 - Pop 动画 */
.page-pop-enter-active {
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.page-pop-leave-active {
  transition: all 0.3s ease;
}
.page-pop-enter-from {
  opacity: 0;
  transform: scale(0.8);
}
.page-pop-leave-to {
  opacity: 0;
  transform: scale(1.1);
}

/* 方案4：弹性动画 - 活泼 */
.page-bounce-enter-active {
  animation: bounceIn 0.5s ease;
}
.page-bounce-leave-active {
  animation: bounceOut 0.3s ease;
}
@keyframes bounceIn {
  0% {
    opacity: 0;
    transform: scale(0.3) translate3d(0, 20px, 0);
  }
  50% {
    opacity: 1;
    transform: scale(1.05) translate3d(0, -5px, 0);
  }
  70% {
    transform: scale(0.95) translate3d(0, 2px, 0);
  }
  100% {
    opacity: 1;
    transform: scale(1) translate3d(0, 0, 0);
  }
}
@keyframes bounceOut {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.8);
  }
}

/* 方案5：3D翻转 - 炫酷 */
.page-flip-enter-active,
.page-flip-leave-active {
  transition: all 0.6s ease;
  transform-style: preserve-3d;
}
.page-flip-enter-from {
  opacity: 0;
  transform: perspective(800px) rotateY(30deg);
}
.page-flip-leave-to {
  opacity: 0;
  transform: perspective(800px) rotateY(-30deg);
}

/* 方案6：上下滑动 - 简洁 */
.page-updown-enter-active,
.page-updown-leave-active {
  transition: all 0.4s ease;
}
.page-updown-enter-from {
  transform: translateY(20px);
  opacity: 0;
}
.page-updown-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}
</style>